import {
  CSSProperties,
  RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
// One reading of what is on screen, shared with the hook that lifts the field
// being typed into clear of the keyboard: a list that opens behind a keyboard
// the field was just scrolled out of is the same bug twice. It also carries
// the answer for the in-app browsers, which report no keyboard at all — see
// `visibleBand`.
import { TYPED_INTO, visibleBand } from "../../lib/viewport";
import type { Band } from "../../lib/viewport";

/**
 * Tracks where a floating panel should sit relative to its trigger.
 *
 * Panels that belong to a table row have to be portalled out to `document.body`
 * — the table body is an `overflow-auto` scroll container, so an absolutely
 * positioned panel would be clipped on the last few rows. Once portalled the
 * panel is `position: fixed`, which is why the trigger has to be measured in
 * viewport coordinates and re-measured whenever anything moves.
 *
 * @param maxHeight tallest the panel can get, in px. Used to decide whether
 *                  there is room below the trigger or it has to flip upward.
 */
/** Whether two measurements would put the panel in the same place. */
const samePlace = (a: DOMRect | null, b: DOMRect): boolean =>
  !!a &&
  a.top === b.top &&
  a.left === b.left &&
  a.width === b.width &&
  a.height === b.height;

/**
 * Phone widths — Tailwind's `sm:` breakpoint less a pixel, so this and every
 * `sm:` rule in the app change over together.
 */
const PHONE = "(max-width: 639px)";

/**
 * Whether a panel should be drawn as a sheet at the foot of the screen instead
 * of hung off the trigger it belongs to.
 *
 * A panel anchored to its trigger is the right answer on a desktop and an
 * unwinnable one on a phone. The trigger is a form field a keyboard is very
 * often covering, and the room left in front of it is a strip a few hundred
 * pixels tall that every browser reports differently and half of them scroll
 * out from under the panel as it opens: the list ends up above the field it
 * belongs to, or off the bottom of the screen, or hanging over the middle of
 * the form beside a field it has nothing to do with — which is the whole of
 * the complaint this answers.
 *
 * A sheet does not have the question. It is measured off the screen rather
 * than off the trigger, so where the field ended up, and whether the browser
 * moved it, stops mattering: the list is at the bottom of what is visible,
 * above the keyboard, every time. Which is also what a phone reader expects,
 * because it is what the platform's own pickers and every app they use do.
 *
 * jsdom ships no `matchMedia`, so the test runner answers "not a phone" — the
 * same reading `DesktopOnly` takes of the same silence.
 */
const useSheet = (): boolean => {
  const [sheet, setSheet] = useState(() =>
    typeof window === "undefined" || typeof window.matchMedia !== "function"
      ? false
      : window.matchMedia(PHONE).matches
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;

    const list = window.matchMedia(PHONE);
    const update = () => setSheet(list.matches);

    // Re-read on mount as well: the width can have changed between the state
    // initialiser above and this effect.
    update();

    // Safari only gained `addEventListener` on MediaQueryList in 14, and the
    // deprecated `addListener` is all that is there before it.
    if (typeof list.addEventListener === "function") {
      list.addEventListener("change", update);
      return () => list.removeEventListener("change", update);
    }
    list.addListener(update);
    return () => list.removeListener(update);
  }, []);

  return sheet;
};

const useAnchoredPanel = <T extends HTMLElement>(
  anchorRef: RefObject<T | null>,
  open: boolean,
  maxHeight: number
) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [band, setBand] = useState<Band>(visibleBand);
  const sheet = useSheet();

  // Layout effect, not effect: the panel must not paint at a stale position
  // for a frame before snapping to the right one.
  useLayoutEffect(() => {
    if (!open) {
      setRect(null);
      return;
    }

    const measure = () => {
      // Ahead of the anchor and unconditionally: a keyboard opening changes
      // where there is room for the panel without moving the trigger by a
      // pixel, and a trigger that has left the document still leaves a band
      // worth keeping current for the next one.
      setBand((current) => {
        const next = visibleBand();
        return current.top === next.top && current.bottom === next.bottom
          ? current
          : next;
      });

      const anchor = anchorRef.current;
      // A trigger the document no longer holds cannot be measured: it answers
      // with a rect of zeroes, and there is nothing gentle about that here.
      // The app shell is `fixed inset-0`, so the document behind the portal
      // has no height of its own, and a panel handed zeroes does not land near
      // the row it belongs to — it lands at the top of the page. Keeping the
      // last good measurement is the lesser wrong.
      if (!anchor?.isConnected) return;

      const next = anchor.getBoundingClientRect();

      // Same place, same object: this runs on every scroll event the page
      // sees, and a fresh DOMRect each time would re-render the panel for a
      // position it is already at.
      setRect((current) => (samePlace(current, next) ? current : next));
    };

    measure();

    // And again on the next frame. The first pass runs inside the click that
    // opened the panel, which is exactly the moment the row under it may still
    // be settling — a table that has just re-flowed, an overlay scrollbar that
    // has just appeared under the pointer. Whatever that pass caught, this one
    // is the position the browser actually finished the frame at.
    const frame = requestAnimationFrame(measure);

    // Capture phase, so scrolling any ancestor container moves the panel with
    // its trigger — not just scrolling the window.
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    // The window does not resize when a keyboard opens over it — only this
    // does. Without it a list opened before the keyboard finished sliding up
    // would keep the placement it chose while there was still room below.
    window.visualViewport?.addEventListener("resize", measure);
    // And a phone browser clearing a focused field of the keyboard does not
    // always scroll the document to do it: several scroll the *visual*
    // viewport across the layout one instead, which fires here and nowhere
    // else — no window `scroll`, no `resize`, nothing the line above would
    // catch. A panel that missed it kept the placement it was opened at while
    // the field it belongs to slid up the screen without it, which is a list
    // floating over the middle of a form beside a field it has nothing to do
    // with.
    window.visualViewport?.addEventListener("scroll", measure);
    // And in the browsers that report no keyboard at all, the caret moving is
    // the only news there is: an in-app browser fires no resize and no scroll
    // when its keyboard slides up, so the one thing that tells a panel the
    // screen is now half the size it was is the field that was focused to
    // bring the keyboard up. On the window, so a sheet handing focus to its
    // own filter box counts as well. See `visibleBand`, which is what turns
    // the two into a measurement.
    window.addEventListener("focusin", measure);
    window.addEventListener("focusout", measure);

    /**
     * And a trigger can move with nothing scrolling and no window changing
     * size at all: a card above it grows, a logo finally loads, a step
     * re-flows, a field two rows up turns red and gets a line taller. None of
     * those fire a scroll or a resize, so a panel placed before one of them
     * kept the position it was opened at while the field it belongs to walked
     * out from under it — a list floating half a screen from its own field,
     * which is what this is here to stop.
     *
     * The anchor for its own size, and the document for everything above it.
     * Neither fires while nothing is moving.
     */
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (observer) {
      if (anchorRef.current) observer.observe(anchorRef.current);
      observer.observe(document.documentElement);
    }

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("scroll", measure);
      window.removeEventListener("focusin", measure);
      window.removeEventListener("focusout", measure);
      observer?.disconnect();
    };
  }, [open, anchorRef]);

  // Flip up only when below is genuinely too tight *and* above has more room,
  // so a trigger near the middle of a short viewport keeps opening downward.
  //
  // Both measured against what is on screen rather than against the window, so
  // the strip of page a keyboard is covering does not count as room. Same sum
  // as before wherever there is no keyboard: the band is then the window.
  const below = rect ? band.bottom - rect.bottom : 0;
  const above = rect ? rect.top - band.top : 0;
  const openUpward = !!rect && below < maxHeight + 8 && above > below;

  // A sheet needs neither of the two numbers above, and nothing else here
  // either: it is not hung off the trigger, and it is not placed against the
  // keyboard — see `sheetStyle`, which measures a sheet off the screen alone.
  // What it does still want is the render a rotation causes, which the resize
  // listener above gives it.
  return { rect, openUpward, sheet };
};

/** The gap between a trigger and the panel that belongs to it. */
const GAP = 4;
/** How close to the edge of the screen a panel is allowed to come. */
const EDGE = 8;
/**
 * The least a panel may be squeezed to.
 *
 * Only reached on a viewport with almost nothing in either direction — a
 * landscape phone with a keyboard up. Two rows and a scroll is a worse list
 * than a tall one, and still a list; a panel capped to the four pixels that
 * were actually free would be nothing at all.
 */
const MIN_HEIGHT = 96;

const clamp = (value: number, low: number, high: number): number =>
  Math.min(Math.max(value, low), Math.max(low, high));

/**
 * Fixed-position style for a panel anchored under (or over) `rect`.
 *
 * Two things here beyond the position itself, and both are about the panel
 * staying on the screen it is drawn on:
 *
 * `maxHeight` is what the caller would like, not what it gets. A list asking
 * for 320px above a trigger with 180px of screen over it used to be drawn at
 * its full height, so the top of it — the first options, the ones somebody is
 * most likely to want — was off the top edge with no way to scroll to it. The
 * panel is capped to the room actually in front of it, and scrolls inside
 * whatever it was given.
 *
 * And the anchor is clamped into the visible band before anything is measured
 * off it. A trigger rect can be stale by a frame — a page that scrolled under
 * a panel already open, a container that re-flowed — and a panel hung off a
 * rect that is no longer on screen lands nowhere near the field it belongs to.
 * Clamped, the worst case is a panel at the edge of the screen instead of
 * beyond it.
 */
/**
 * The strip of page left showing above a sheet, so it reads as something laid
 * over the form rather than as a new screen — the field it belongs to is very
 * often what is in that strip.
 */
const SHEET_HEADROOM = 72;

/**
 * Tallest a sheet gets on a screen with room to spare. A list is easier to
 * read than it is to scroll, so it takes what it can — but a sheet that is
 * most of the screen has stopped being a sheet, and the page behind it is what
 * says the form is still there and still where it was left.
 */
const SHEET_MAX_HEIGHT = 420;

/**
 * Fixed-position style for a panel drawn as a sheet at the foot of the screen.
 *
 * On the bottom edge of the screen, edge to edge, and measured against nothing
 * else — which is the whole of what makes it reliable. No amount of scrolling,
 * no browser clearing a field its own way, and no field left under a keyboard
 * can put it anywhere but there.
 *
 * It used to stand on the keyboard instead, on the reasoning that one left up
 * by the field before this one is still ground a list may not open onto. The
 * reasoning was sound; the ground was not. A sheet only ever opens from a tap
 * on a control with nothing to type into, and that tap is the moment the
 * keyboard starts going away — so the reading it was placed against was
 * already out of date by the time it was taken, and in two different ways:
 *
 * A browser that reports its keyboard was still reporting the one it had just
 * been asked to put away, so the sheet opened a keyboard's height up the
 * screen and dropped to the floor a moment later. That drop is the jump.
 *
 * And the in-app browsers, which are most of how a shared form link is ever
 * opened, report no keyboard at all: `visibleBand` falls back to assuming half
 * the window is covered for as long as something is focused, and the field
 * before it keeps that focus, because a tap on a `div` does not take it away
 * in every browser — and a `div` is what `MultiSelect`'s trigger is. There the
 * sheet opened halfway up the screen, beside the form rather than over the
 * foot of it, and stayed: nothing was ever going to fire to correct it.
 *
 * Neither can happen to a sheet on the bottom edge. The keyboard is taken down
 * by the control as the sheet opens — see `dismissKeyboard` — so the floor is
 * the floor, and the sheet is on it, which is where a phone reader is already
 * looking for it.
 */
export const sheetStyle = (): CSSProperties => {
  // The window, not the visible strip: a keyboard is on its way out by the
  // time this is read, and the strip is what it was covering. Measuring
  // against the strip is what put the sheet where the keyboard had been.
  const room = window.innerHeight - SHEET_HEADROOM;
  const height = Math.max(MIN_HEIGHT, Math.min(SHEET_MAX_HEIGHT, room));

  return {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    maxHeight: height,
  };
};

/**
 * The whole screen, for the picker a phone opens over a list that is typed
 * into.
 *
 * Deliberately nothing but `inset: 0` — no viewport read, no keyboard height,
 * no correction. Every version of this that tried to float a panel in the strip
 * above the keyboard failed somewhere: the in-app browsers do not say where the
 * keyboard is, iOS moves the ground under a fixed element while it is open, and
 * a panel that guesses wrong is a list hanging in the middle of a form or a box
 * being typed into behind the keys.
 *
 * A picker that takes the screen has no such question to get wrong. The
 * keyboard covers the foot of it, which costs nothing: what has to stay in
 * sight is the box being typed into and the first rows under it, and those are
 * at the top, where no keyboard has ever reached. The list scrolls under the
 * keys the way it does in every phone application that asks you to pick from
 * more than a screenful.
 *
 * It is also self-righting on iOS, which is the browser that moves things
 * about. Safari scrolls a focused field into view; the field here is pinned to
 * the top of the screen, so whatever Safari did to the page for the last one is
 * undone on the way in.
 */
/** How many goes a takeover gets at finding the top of the screen. */
const TOP_CORRECTIONS = 3;

/**
 * A screen-filling picker, with its top edge on the top of what can be seen.
 *
 * `inset: 0` alone is not that. A phone browser's own toolbar overlaps the
 * viewport a fixed element is placed in — Chrome slides its address bar back in
 * as you scroll up, and the room it takes comes off the top of what is visible
 * without the page being told to move. So a picker pinned to `top: 0` opened
 * with its heading and its search box behind the browser's own search box.
 *
 * The top of the visible strip is a figure every browser reports, so this one
 * is not guessed at. But nor is it simply applied: browsers differ over whether
 * a fixed element is placed against the layout viewport or the visible strip
 * inside it, and on the ones that already do the right thing, applying it again
 * would push the picker down by as much as it was meant to rise.
 *
 * So the picker is placed, then asked where its top edge landed, and moved by
 * the difference. A browser that had it right answers with no difference and
 * nothing happens; one that had it behind its toolbar answers with the toolbar,
 * and gets it back. Neither has to be identified.
 */
export const useTakeoverStyle = (
  panelRef: RefObject<HTMLElement | null>,
  open: boolean
): CSSProperties => {
  const [top, setTop] = useState(0);
  /** Corrections spent on the target below, which is reset with it. */
  const spent = useRef(0);
  const aimedAt = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      spent.current = 0;
      aimedAt.current = null;
      setTop((current) => (current === 0 ? current : 0));
      return;
    }

    const panel = panelRef.current;
    if (!panel) return;

    // Held at the top of itself. A picker is a fixed frame with one scrolling
    // list inside it, and `overflow-hidden` hides a scrollbar rather than
    // refusing to scroll — so a browser bringing the focused box into view can
    // nudge the frame itself, which slides the heading and the box being typed
    // into off the screen while everything below them stays put.
    if (panel.scrollTop !== 0) panel.scrollTop = 0;

    const landed = panel.getBoundingClientRect();
    // A rect of zeroes is not a measurement — jsdom lays nothing out, and an
    // element mid-mount can say the same.
    if (landed.height <= 0) return;

    const wanted = visibleBand().top;
    // A toolbar sliding in or out is a new target, and gets a fresh allowance:
    // it can happen many times while one picker is open.
    if (aimedAt.current !== wanted) {
      aimedAt.current = wanted;
      spent.current = 0;
    }

    const off = wanted - landed.top;
    if (Math.abs(off) <= 1 || spent.current >= TOP_CORRECTIONS) return;

    spent.current += 1;
    setTop((current) => current + off);
    // `top` is a dependency so that applying a correction is followed by a look
    // at what it achieved — see the allowance above, which is what stops a
    // browser that will not settle being argued with forever.
  }, [open, top, panelRef]);

  return { position: "fixed", top, right: 0, bottom: 0, left: 0 };
};

/**
 * Puts away a keyboard the field before this one left up.
 *
 * A sheet opens from a control with nothing to type into, so a keyboard over
 * it is never wanted and never has to be measured around — but only if it has
 * actually gone. Most browsers take it down themselves when a tap lands on
 * something that is not a field. The ones this matters most for do not: an
 * in-app browser leaves the caret where it was when the tap was on a `div`,
 * and a keyboard nobody has taken down is one `visibleBand` goes on assuming
 * is covering half the screen for as long as the sheet is open.
 *
 * Called from the tap that opens the sheet rather than from an effect behind
 * it, so the blur is traceable to the gesture — which is what a phone browser
 * asks for before it will move a keyboard either way.
 */
export const dismissKeyboard = (): void => {
  const field = document.activeElement;
  if (field instanceof HTMLElement && field.matches(TYPED_INTO)) field.blur();
};

export const anchoredPanelStyle = (
  rect: DOMRect,
  openUpward: boolean,
  maxHeight: number,
  width?: number
): CSSProperties => {
  const band = visibleBand();

  // Where the panel hangs from, held on screen — see above.
  const anchorTop = clamp(rect.top, band.top, band.bottom);
  const anchorBottom = clamp(rect.bottom, band.top, band.bottom);

  const room = openUpward
    ? anchorTop - band.top - GAP - EDGE
    : band.bottom - anchorBottom - GAP - EDGE;

  return {
    position: "fixed",
    left: rect.left,
    width: width ?? rect.width,
    maxHeight: Math.max(Math.min(maxHeight, room), MIN_HEIGHT),
    // Upward is anchored by its bottom edge rather than by a top worked out
    // from the height, so a list of two options sits against the trigger
    // instead of floating a panel's worth of nothing above it.
    ...(openUpward
      ? { bottom: window.innerHeight - anchorTop + GAP }
      : { top: anchorBottom + GAP }),
  };
};

export default useAnchoredPanel;
